import type { MapleMythosRunState } from './types';

export interface MapleMythosKnowledgeBaseDocumentDraft {
  name: string;
  content: string;
  fileType: string;
  size: number;
}

function sanitizeSegment(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

function summarizePrompt(prompt: string) {
  const compact = prompt.replace(/\s+/g, ' ').trim();
  if (!compact) return 'run';
  return compact.length > 60 ? `${compact.slice(0, 60)}…` : compact;
}

function buildTranscriptMarkdown(run: MapleMythosRunState) {
  return [
    `# ${run.title || 'DeMythos Run'} Transcript`,
    '',
    `- Run ID: ${run.id}`,
    `- Status: ${run.status}`,
    `- Model: ${run.modelName}`,
    `- Loops completed: ${run.currentLoop}/${run.maxLoops}`,
    `- Started: ${run.startedAt}`,
    `- Updated: ${run.updatedAt}`,
    `- SSH grant: ${run.sshGrant ? `${run.sshGrant.name} (${run.sshGrant.username}@${run.sshGrant.host}:${run.sshGrant.port})` : 'none'}`,
    '',
    '## User Task',
    '',
    run.prompt,
    '',
    '## Transcript',
    '',
    ...run.transcript.flatMap((entry) => {
      const headerParts = [entry.kind, entry.phase, typeof entry.loopIndex === 'number' ? `loop ${entry.loopIndex}` : null]
        .filter(Boolean)
        .join(' · ');

      const block = [
        `### ${headerParts || 'entry'} — ${entry.timestamp}`,
        '',
        entry.message,
      ];

      if (entry.content) {
        block.push('', '```text', entry.content, '```');
      }

      return [...block, ''];
    }),
  ].join('\n');
}

export function buildMapleMythosKnowledgeBaseName(run: MapleMythosRunState) {
  const sshLabel = run.sshGrant ? sanitizeSegment(run.sshGrant.name) : 'local';
  const promptLabel = sanitizeSegment(summarizePrompt(run.prompt)) || 'run';
  const stamp = run.startedAt.replace(/[:T]/g, '-').slice(0, 16);
  return `DeMythos Findings ${stamp} ${sshLabel} ${promptLabel}`.trim();
}

export function buildMapleMythosKnowledgeBaseDescription(run: MapleMythosRunState) {
  return [
    `DeMythos findings captured from run ${run.id}.`,
    run.sshGrant ? `This run used the granted SSH profile ${run.sshGrant.name} (${run.sshGrant.username}@${run.sshGrant.host}:${run.sshGrant.port}).` : 'This run did not use a granted SSH profile.',
    'Intended for documentation, review, and follow-up research.',
  ].join(' ');
}

export function buildMapleMythosKnowledgeBaseDocuments(run: MapleMythosRunState): MapleMythosKnowledgeBaseDocumentDraft[] {
  const findings = [
    `# ${run.title || 'DeMythos Findings'}`,
    '',
    `- Run ID: ${run.id}`,
    `- Status: ${run.status}`,
    `- Model: ${run.modelName}`,
    `- Execution mode: ${run.executionMode}`,
    `- Loops: ${run.currentLoop}/${run.maxLoops}`,
    `- Tokens used: ${run.tokensUsed}`,
    `- Credits used: ${run.creditsUsed}`,
    `- Started: ${run.startedAt}`,
    `- Updated: ${run.updatedAt}`,
    `- SSH grant: ${run.sshGrant ? `${run.sshGrant.name} (${run.sshGrant.username}@${run.sshGrant.host}:${run.sshGrant.port})` : 'none'}`,
    '',
    '## Prompt',
    '',
    run.prompt,
    '',
    '## Summary',
    '',
    run.finalSummary || run.workingState.latestSummary || 'No summary was produced.',
    '',
    '## Findings',
    '',
    run.finalAnswer || run.workingState.latestAnswer || 'No final answer was produced.',
    '',
    '## Latest Plan',
    '',
    run.workingState.latestPlan || 'None.',
    '',
    '## Latest Critique',
    '',
    run.workingState.latestCritique || 'None.',
    '',
  ].join('\n');

  const transcript = buildTranscriptMarkdown(run);
  const state = JSON.stringify(
    {
      id: run.id,
      prompt: run.prompt,
      status: run.status,
      modelName: run.modelName,
      executionMode: run.executionMode,
      startedAt: run.startedAt,
      updatedAt: run.updatedAt,
      sshGrant: run.sshGrant,
      finalSummary: run.finalSummary,
      finalAnswer: run.finalAnswer,
      workingState: run.workingState,
      transcript: run.transcript,
    },
    null,
    2,
  );

  return [
    {
      name: 'findings.md',
      content: findings,
      fileType: 'markdown',
      size: findings.length,
    },
    {
      name: 'transcript.md',
      content: transcript,
      fileType: 'markdown',
      size: transcript.length,
    },
    {
      name: 'run-state.json',
      content: state,
      fileType: 'json',
      size: state.length,
    },
  ];
}
